import React, { useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send } from 'lucide-react';
import { useConnect } from '../context/ConnectContext';
import { createInquiry } from '../lib/api';
import { saveInquiry } from '../data/mock';

const empty = { firstName: '', lastName: '', email: '', message: '' };

export default function ConnectModal() {
  const { open, setOpen } = useConnect();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email) {
      toast.error('Please add your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      await createInquiry(form);
      saveInquiry(form); // keep a local copy as a lightweight fallback
      setForm(empty);
      setOpen(false);
      toast.success('Thank you. The Farmway team will be in touch shortly.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg overflow-hidden border-white/10 bg-farmway-green-dark p-0 text-farmway-cream">
        <div className="bg-gradient-to-br from-farmway-green-mid to-farmway-green-dark px-8 pb-2 pt-8">
          <DialogHeader className="text-left">
            <span className="eyebrow">Get in touch</span>
            <DialogTitle className="font-display text-3xl font-black uppercase tracking-tight text-farmway-cream">
              Let&apos;s grow together
            </DialogTitle>
            <DialogDescription className="text-farmway-cream/70">
              Tell us a little about your interest in farmland investing with Farmway.
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={onSubmit} className="space-y-5 px-8 pb-8 pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-farmway-cream/80">First name</Label>
              <Input id="firstName" value={form.firstName} onChange={update('firstName')} placeholder="Jane"
                className="border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-farmway-cream/80">Last name</Label>
              <Input id="lastName" value={form.lastName} onChange={update('lastName')} placeholder="Doe"
                className="border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-farmway-cream/80">Email</Label>
            <Input id="email" type="email" value={form.email} onChange={update('email')} placeholder="jane@company.com"
              className="border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className="text-farmway-cream/80">Your message</Label>
            <Textarea id="message" value={form.message} onChange={update('message')} rows={4}
              placeholder="I'd like to learn more about..."
              className="resize-none border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold" />
          </div>
          <button type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-70">
            {submitting ? 'Sending…' : (<>Submit <Send className="h-4 w-4" /></>)}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
